import { Search } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

interface UserFiltersProps {
  searchTerm: string;
  setSearchTerm: (value: string) => void;
  statusFilter: "all" | "active" | "inactive";
  setStatusFilter: (value: "all" | "active" | "inactive") => void;
  roleFilter: "all" | "admin" | "user";
  setRoleFilter: (value: "all" | "admin" | "user") => void;
  counts: {
    total: number;
    active: number;
    inactive: number;
    admins: number;
  };
  onReset: () => void;
}

const filterButtonClassName =
  "rounded-lg border-gray-200 text-gray-700 hover:border-whatsapp hover:text-whatsapp";

const activeButtonClassName = "rounded-lg bg-whatsapp text-white hover:bg-whatsapp-dark";

const UserFilters = ({
  searchTerm,
  setSearchTerm,
  statusFilter,
  setStatusFilter,
  roleFilter,
  setRoleFilter,
  counts,
  onReset,
}: UserFiltersProps) => {
  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
        <div className="w-full max-w-md">
          <label className="mb-2 block text-sm font-medium text-gray-700">Search users</label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <Input
              type="search"
              placeholder="Search by name or email"
              className="h-11 rounded-lg border-gray-200 bg-white pl-9"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
            />
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <div className="mb-2 text-sm font-medium text-gray-700">Status</div>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant={statusFilter === "all" ? "default" : "outline"}
                size="sm"
                onClick={() => setStatusFilter("all")}
                className={statusFilter === "all" ? activeButtonClassName : filterButtonClassName}
              >
                All
                <span className="ml-1 rounded-full bg-black/10 px-2 py-0.5 text-[11px] font-semibold">{counts.total}</span>
              </Button>
              <Button
                type="button"
                variant={statusFilter === "active" ? "default" : "outline"}
                size="sm"
                onClick={() => setStatusFilter("active")}
                className={statusFilter === "active" ? activeButtonClassName : filterButtonClassName}
              >
                Active
                <span className="ml-1 rounded-full bg-black/10 px-2 py-0.5 text-[11px] font-semibold">{counts.active}</span>
              </Button>
              <Button
                type="button"
                variant={statusFilter === "inactive" ? "default" : "outline"}
                size="sm"
                onClick={() => setStatusFilter("inactive")}
                className={statusFilter === "inactive" ? activeButtonClassName : filterButtonClassName}
              >
                Inactive
                <span className="ml-1 rounded-full bg-black/10 px-2 py-0.5 text-[11px] font-semibold">{counts.inactive}</span>
              </Button>
            </div>
          </div>

          <div>
            <div className="mb-2 text-sm font-medium text-gray-700">Role</div>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant={roleFilter === "all" ? "default" : "outline"}
                size="sm"
                onClick={() => setRoleFilter("all")}
                className={roleFilter === "all" ? activeButtonClassName : filterButtonClassName}
              >
                All
              </Button>
              <Button
                type="button"
                variant={roleFilter === "admin" ? "default" : "outline"}
                size="sm"
                onClick={() => setRoleFilter("admin")}
                className={roleFilter === "admin" ? activeButtonClassName : filterButtonClassName}
              >
                Admins
                <span className="ml-1 rounded-full bg-black/10 px-2 py-0.5 text-[11px] font-semibold">{counts.admins}</span>
              </Button>
              <Button
                type="button"
                variant={roleFilter === "user" ? "default" : "outline"}
                size="sm"
                onClick={() => setRoleFilter("user")}
                className={roleFilter === "user" ? activeButtonClassName : filterButtonClassName}
              >
                Users
              </Button>
            </div>
          </div>
        </div>
      </div>

      {(searchTerm || statusFilter !== "all" || roleFilter !== "all") && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 rounded-lg px-0 text-gray-600 hover:bg-transparent hover:text-whatsapp"
          onClick={onReset}
        >
          Clear filters
        </Button>
      )}
    </div>
  );
};

export default UserFilters;
